import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { EchartsWithTheme, Empty, useTheme } from '@maxtropy/components';
import styles from './index.module.scss';
import { ShareDataUnityPowerMapContext, IndexTimeActive } from '../../../..';
import { getDefaultTime, calcPercent, calcRatio } from '@/pages/WgqAllMaps/utils';
import {
  apiV2ElectricMapDataElectricityOverviewPost,
  V2ElectricMapDataElectricityOverviewPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { Spin } from 'antd';
import { getPieChartOption } from '../chart';
import circleImg from '../../../../../../imgs/circle@2x.png';
import titleIcon from '@/assets/images/title_icon.png';
import { keepOneAndWan } from '@/utils/utils';
import { isNil } from 'lodash-es';

const PeakValleyLeft = () => {
  const theme = useTheme();
  const { timeResolution: activeKey, dateValue: value } = useContext(ShareDataUnityPowerMapContext);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [originData, setOriginData] = React.useState<V2ElectricMapDataElectricityOverviewPostResponse>();

  useEffect(() => {
    if (!value || !activeKey) return;
    setLoading(true);
    apiV2ElectricMapDataElectricityOverviewPost({
      timeResolution: activeKey === IndexTimeActive.THIS_MONTH ? 'M' : 'Y',
      start: getDefaultTime(activeKey, value).startTime,
      end: getDefaultTime(activeKey, value).endTime,
    })
      .then(res => {
        setOriginData(res);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [activeKey, value]);

  const chartPieOption = useMemo(() => getPieChartOption(originData), [originData]);

  const isAllNull = useMemo(() => {
    return isNil(originData?.peak) && isNil(originData?.summit) && isNil(originData?.plain);
  }, [originData]);

  const calValley = () => {
    const peakRatio = calcPercent(originData?.peak, originData?.total);
    const summitRatio = calcPercent(originData?.summit, originData?.total);
    const plainRatio = calcPercent(originData?.plain, originData?.total);
    return isAllNull ? '--' : `${(100 - peakRatio - summitRatio - plainRatio).toFixed(1)}%`;
  };

  return (
    <Spin spinning={loading}>
      <div className={styles.container}>
        <div className={styles.titleRow}>
          <img src={titleIcon} alt="" />
          <div>峰谷占比</div>
        </div>
        <div className={styles.box}>
          {/* 尖电量 */}
          <div className={styles.topLeftHorn}>
            <div className={styles.label}>尖电量(kWh)</div>
            <div className={styles.power}>
              {keepOneAndWan(originData?.summit).value}
              <span className={styles.unit}>{keepOneAndWan(originData?.summit).unit}</span>
            </div>
            <div className={styles.rate}>{calcRatio(originData?.summit, originData?.total)}</div>
          </div>
          {/* 峰电量 */}
          <div className={styles.topRightHorn}>
            <div className={styles.label}>峰电量(kWh)</div>
            <div className={styles.power}>
              {keepOneAndWan(originData?.peak).value}
              <span className={styles.unit}>{keepOneAndWan(originData?.peak).unit}</span>
            </div>
            <div className={styles.rate}>{calcRatio(originData?.peak, originData?.total)}</div>
          </div>
          {/* 平电量 */}
          <div className={styles.bottomLeftHorn}>
            <div className={styles.label}>平电量(kWh)</div>
            <div className={styles.power}>
              {keepOneAndWan(originData?.plain).value}
              <span className={styles.unit}>{keepOneAndWan(originData?.plain).unit}</span>
            </div>
            <div className={styles.rate}>{calcRatio(originData?.plain, originData?.total)}</div>
          </div>
          {/* 谷电量 */}
          <div className={styles.bottomRightHorn}>
            <div className={styles.label}>谷电量(kWh)</div>
            <div className={styles.power}>
              {keepOneAndWan(originData?.valley).value}
              <span className={styles.unit}>{keepOneAndWan(originData?.valley).unit}</span>
            </div>
            <div className={styles.rate}>{calValley()}</div>
          </div>
          {/* 饼图 */}
          <div className={styles.circle}>
            <EchartsWithTheme
              theme={theme}
              type="pie"
              style={{ height: 200, width: 200 }}
              option={chartPieOption}
              empty={{
                isShowEmpty: isAllNull,
                emptyNode: <Empty />,
              }}
            />
            <img src={circleImg} alt="" className={styles.rotateCircle} />
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default PeakValleyLeft;
